import React, { useEffect, useRef, useState } from "react";
import { Mic, Check, Loader2 } from "lucide-react";

export function permissionErrorMessage(e, kind = "camera and microphone") {
  const name = e?.name || "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return `Access to your ${kind} was blocked. Click the lock or camera icon in your browser's address bar, set Camera and Microphone to Allow, then press Retry.`;
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
    return `No ${kind} was found. Connect or enable a device in your system settings, then press Retry.`;
  }
  if (name === "NotReadableError" || name === "AbortError") {
    return `Your ${kind} is already in use by another app (like Zoom or Teams). Close that app, then press Retry.`;
  }
  return `We couldn't access your ${kind}. Check your browser permissions, then press Retry.`;
}

/** Live microphone level indicator driven by an AnalyserNode on the stream. */
export const MicLevelMeter = ({ stream, className = "" }) => {
  const [level, setLevel] = useState(0);
  const lastRef = useRef(-1);

  useEffect(() => {
    lastRef.current = -1;
    setLevel(0);
    if (!stream || !(stream.getAudioTracks?.() || []).length) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    let ctx, src, analyser;
    try {
      ctx = new Ctx();
      src = ctx.createMediaStreamSource(stream);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
    } catch {
      return;
    }
    ctx.resume?.().catch(() => {});
    const data = new Uint8Array(analyser.fftSize);
    let raf;
    let alive = true;
    const tick = () => {
      if (!alive) return;
      analyser.getByteTimeDomainData(data);
      let peak = 0;
      for (let i = 0; i < data.length; i += 4) {
        const d = Math.abs(data[i] - 128);
        if (d > peak) peak = d;
      }
      const q = Math.min(5, Math.round((peak / 40) * 5));
      if (q !== lastRef.current) {
        lastRef.current = q;
        setLevel(q);
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      alive = false;
      if (raf) cancelAnimationFrame(raf);
      try { src.disconnect(); } catch { /* noop */ }
      try { ctx.close(); } catch { /* noop */ }
    };
  }, [stream]);

  return (
    <div
      data-testid="mic-level-meter"
      title="Microphone level"
      className={`inline-flex items-center gap-1.5 rounded-full bg-zinc-950/80 border border-zinc-800 px-2 py-1 ${className}`}
    >
      <Mic className={`h-3 w-3 ${level > 0 ? "text-emerald-400" : "text-zinc-500"}`} />
      <span className="flex items-end gap-[2px]" aria-hidden>
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            style={{ height: `${3 + i * 2}px` }}
            className={`w-[3px] rounded-sm transition-colors ${level >= i ? "bg-emerald-400" : "bg-zinc-700"}`}
          />
        ))}
      </span>
    </div>
  );
};

/** Per-answer processing stepper: Uploading (real %) → Transcribing → Evaluating. */
export const UploadStages = ({ phase, pct = 0 }) => {
  const steps = [
    { id: "uploading", label: phase === "uploading" ? `Uploading ${Math.min(99, pct)}%` : "Uploaded" },
    { id: "transcribing", label: "Transcribing" },
    { id: "evaluating", label: "Evaluating" },
  ];
  const activeIdx = Math.max(0, steps.findIndex((s) => s.id === phase));
  return (
    <div data-testid="upload-stages" className="flex items-center gap-2 flex-wrap">
      {steps.map((s, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <React.Fragment key={s.id}>
            {i > 0 && <span className={`h-px w-4 sm:w-6 ${done || active ? "bg-amber-500/50" : "bg-zinc-800"}`} />}
            <span
              data-testid={`upload-stage-${s.id}`}
              className={`inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full border px-2.5 py-1 transition-colors ${
                done
                  ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                  : active
                    ? "border-amber-500/40 bg-amber-500/[0.08] text-amber-300"
                    : "border-zinc-800 text-zinc-600"
              }`}
            >
              {done ? (
                <Check className="h-3 w-3" />
              ) : active ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-zinc-700" />
              )}
              {s.label}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};
